import type { AccommodationTypeAndSubTypeId } from "@@/shared/acquire/resources/resource-accommodation/accommodation-types/accommodation-type-and-sub-type-id";
import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { AccommodationPurpose } from "@@/shared/orders/order-accommodation.target-group";
import { accommodationPurposeZodSchema } from "@@/shared/orders/order-accommodation.target-group";
import { translatableStringFactory } from "@@/shared/translations/translation";
import { shortId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import { cartIdZodSchema, type CartId } from "../carts/cart-id";
import { type DurationInMinutes } from "../date-time/time-spans";
import { deliveryZodSchema, type Delivery } from "../deliveries/delivery";
import { entityStateZodSchema, type EntityState } from "../entity-status";
import {
    contactInfoZodObject,
    type ContactInfo,
} from "../locations/contact-info";
import type { OrderGroupId } from "../order-groups/order-group-id";
import { orderGroupIdZodSchema } from "../order-groups/order-group-id";
import { priceZodSchema, zeroPrice, type Price } from "../prices/price";
import { productFormIdZodSchema } from "../product-forms/product-form-id";
import type { ProviderSlug } from "../providers/provider";
import { providerSlugZodSchema, type Provider } from "../providers/provider";
import { providerIdZodSchema, type ProviderId } from "../providers/provider-id";
import { customerIdZodSchema, type CustomerId } from "../users/customer-id";
import { userIdZodSchema, type UserId } from "../users/user-id";
import { calculateOrderPrices } from "./calculate-order-prices";
import { orderIdFactory, orderIdZodSchema, type OrderId } from "./order-id";
import { orderItemZodSchema } from "./order-items/order-item";
import { type OrderItem_V1 } from "./order-items/order-item-v1";
import { type OrderItem_V2 } from "./order-items/order-item-v2";
import {
    orderProjectionZodSchema,
    type OrderProjection,
} from "./order-projections/main/order.projection";
import { orderStatusZodSchema, type OrderStatus } from "./order-state";
import { orderTypeZodSchema, type OrderType } from "./order-type";

const orderSubScope = "orders";

type OrderV1<TOrderItem extends OrderItem_V1 | OrderItem_V2 = OrderItem_V2> = {
    readonly _id: OrderId;
    readonly _type: "ORDER";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _status: EntityState;
    readonly version?: number;
    readonly slug: string;
    /**
     * Added
     * If it doesn't exist, it's a `BUYABLE`
     * @type {OrderType}
     */
    readonly type?: OrderType;
    readonly providerId: ProviderId;
    readonly providerSlug?: ProviderSlug | undefined;
    readonly orderGroupId: OrderGroupId;
    readonly orderGroupReference: string;
    readonly orderItems: TOrderItem[];
    readonly price: Price;
    readonly contactInfo: ContactInfo;
    readonly delivery: Delivery;
    readonly messageToProvider: string | null;
    readonly status: OrderStatus;
    /** @deprecated */
    readonly statusHistory: OrderStatus[];
    readonly tip: number;
    readonly tipPrice: Price;
    readonly totalPrice: Price;
    readonly customerId: UserId | CustomerId;
    readonly prepTime: DurationInMinutes;
    readonly leaveMyStuffOutside?: boolean;
    readonly extraOrderFee?: Price;
    readonly destination?: string;
    /**
     * The cart from which this order was created
     * @type {CartId}
     */
    readonly cartId?: CartId;
    /**
     * The cart hash from which this order was created
     * @type {string}
     */
    readonly cartHash?: string;
    readonly accommodationPurpose?: AccommodationPurpose;
    readonly accommodationSubTypeCount?: Record<
        AccommodationTypeAndSubTypeId,
        number
    >;
};

const orderV1ZodSchema: z.ZodSchema<OrderV1<OrderItem_V2>> = z
    .object({
        _id: orderIdZodSchema,
        _type: z.literal("ORDER"),
        _createdAt: isoAndUnixZodSchema,
        _status: entityStateZodSchema,
        version: z.number().optional(),
        slug: z.string(),
        /**
         * If it doesn't exist, it's a `BUYABLE`
         * @type {OrderType}
         */
        type: orderTypeZodSchema.default("BUYABLE").optional(),
        providerId: providerIdZodSchema,
        providerSlug: providerSlugZodSchema.optional(),
        orderGroupId: orderGroupIdZodSchema,
        orderGroupReference: z.string(),
        orderItems: z.array(orderItemZodSchema),
        price: priceZodSchema,
        contactInfo: contactInfoZodObject,
        delivery: deliveryZodSchema,
        messageToProvider: z.string().nullable(),
        status: orderStatusZodSchema,
        statusHistory: z.array(orderStatusZodSchema),
        tip: z.number(),
        tipPrice: priceZodSchema,
        totalPrice: priceZodSchema,
        customerId: userIdZodSchema.or(customerIdZodSchema), // todo
        prepTime: z
            .number()
            .min(0) as unknown as z.ZodSchema<DurationInMinutes>, // todo
        leaveMyStuffOutside: z.boolean().optional(),
        extraOrderFee: priceZodSchema.optional(),
        destination: z.string().optional(),
        /**
         * The cart from which this order was created
         * @type {CartId}
         */
        cartId: cartIdZodSchema.optional(),
        /**
         * The cart hash from which this order was created
         * @type {string}
         */
        cartHash: z.string().optional(),
        /** Quick fix, should have value only when type is accommodation, defaults to LEISURE */
        accommodationPurpose: accommodationPurposeZodSchema.optional(),
        /**
         * Quick fix
         * Contains count of accommodation type and sub types
         * to be used for e.g. for SCB statistics. \
         * Format: { id: count, ... }
         */
        accommodationSubTypeCount: z
            .record(
                // accommodationTypeAndSubTypeIdZodSchema, Commented out since zod returns a partial record: https://github.com/colinhacks/zod/issues/2623
                z.number(),
            )
            .optional(),
        /** References to product forms, undefined if unknown, null if none, array of form ids if known */
        productFormIds: z.array(productFormIdZodSchema).nullish(),
    })
    .transform((data, ctx) => {
        if (data.type !== "ACCOMMODATION" && !!data.accommodationPurpose) {
            // accommodationPurpose should never be set on a non-accommodation order
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: translatableStringFactory({
                    sv: "Boendemålgrupp kan bara vara sättas när ordern innehåller en boendebokning",
                    en: "Accommodation target group can only be set when the order contains an accommodation booking",
                }),
                path: ["accommodationPurpose"],
            });
            return z.NEVER;
        }
        if (data.type === "ACCOMMODATION" && !data.accommodationPurpose) {
            // Set default value if order is an accommodation order and no value is set
            return { ...data, accommodationPurpose: "LEISURE" };
        }

        // else.. we don't need to do anything
        return data;
    });

const createOrderV1 = (
    params: Except<
        OrderV1,
        | "_id"
        | "_createdAt"
        | "_status"
        | "_type"
        | "slug"
        | "price"
        | "tipPrice"
        | "totalPrice"
        | "statusHistory"
        //      | "events"
    > & {
        orderId?: OrderId;
        creationTimestamp?: IsoAndUnixTimestamp;
        statusHistory?: OrderStatus[];
    },
    provider: Provider,
) => {
    if (params.orderId && !orderIdZodSchema.safeParse(params.orderId).success) {
        throw new Error(
            "Can't create an order with invalid order id; " + params.orderId,
        );
    }

    const justNow = params.creationTimestamp ?? nowIsoAndUnix();
    const orderId = params.orderId ?? orderIdFactory();

    const limit =
        params.delivery.deliveryMethodType === "HOME_DELIVERY"
            ? (provider.options.deliveryOrderAmountLimit ?? zeroPrice)
            : zeroPrice;

    const orderPrices = calculateOrderPrices(
        params.orderItems,
        params.tip,
        limit,
    );

    const result: OrderV1<OrderItem_V2> = {
        _id: orderId,
        _type: "ORDER",
        _status: "ACTIVE",
        slug: shortId(),
        _createdAt: justNow,
        customerId: params.customerId,
        delivery: params.delivery,
        orderGroupId: params.orderGroupId,
        orderGroupReference: params.orderGroupReference,
        orderItems: params.orderItems,
        tip: params.tip,
        type: params.type,
        ...orderPrices,
        messageToProvider: params.messageToProvider || null,
        providerId: params.providerId,
        providerSlug: params.providerSlug,
        contactInfo: params.contactInfo,
        status: params.status,
        prepTime: params.prepTime,
        leaveMyStuffOutside: params.leaveMyStuffOutside,
        destination: params.destination,
        cartId: params.cartId,
        statusHistory: params.statusHistory ?? [],
        accommodationPurpose: params.accommodationPurpose,
        accommodationSubTypeCount: params.accommodationSubTypeCount,
    };

    return result;
};

type Order = OrderProjection;
const orderZodSchema = orderProjectionZodSchema;

export { createOrderV1, orderSubScope, orderV1ZodSchema, orderZodSchema };
export type { Order, OrderV1 };
